import { Element, Nodes } from 'hast';
import { isEmpty, isNil, uniqBy } from 'lodash';
import {
  ComponentProps,
  MouseEvent,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  MarkdownComponents,
  MarkdownContent,
  MarkdownLink,
  MarkdownNodeProps,
  MarkdownTable,
} from '@shared/components/markdown';
import { Colors } from '@shared/styles';
import {
  AskAnswerContext,
  AskCardLink,
  AskCardsContext,
  toCardLink,
  useOpenAskCard,
} from '../cards';
import { splitQuotes } from '../util/splitQuotes';
import styles from './AskAnswerMarkdown.module.css';

/**
 * Whether an answer's code and tables offer a copy button. One value for both, since the table
 * renderer below stands in for the default one and has to offer what it would have.
 */
const COPYABLE = true;

/** The cards of the table a row or link sits in; absent outside a table of cards. */
const CardTableContext = createContext<AskCardLink[] | undefined>(undefined);

/**
 * An Ask answer's markdown, with the cards it names drawn as the app's own card UI rather than as
 * generic markdown.
 *
 * The answer's links are the only structure it carries about cards — see `extractCardLinks` —
 * so that is what this reads:
 *
 * a table whose rows link to cards is drawn the way a workspace's table view is, and a row opens
 * its card.
 *
 * On the Ask page, a card link — a row, its title, or a card named in prose — opens the card over
 * the page with the answer's cards in the pane beside it, as a click on the answer would. In the
 * floating panel the card opens in its workspace (see `useOpenAskCard`). Everything else renders
 * exactly as markdown does anywhere else in the app.
 *
 * This is where more structured output belongs as the gateway grows it: a renderer per thing the
 * client knows how to draw, falling back to plain markdown for everything it does not.
 */
export function AskAnswerMarkdown({ content, messageId }: { content: string; messageId: string }) {
  const answer = useMemo(() => ({ id: messageId, content }), [messageId, content]);
  return (
    <AskAnswerContext.Provider value={answer}>
      <MarkdownContent
        source={content}
        highlightColor={Colors.v2.purple}
        copyable={COPYABLE}
        components={COMPONENTS}
      />
    </AskAnswerContext.Provider>
  );
}

function AskTable({ node, ...props }: MarkdownNodeProps & ComponentProps<'table'>) {
  const cards = useMemo(() => cardLinksIn(node), [node]);

  if (isEmpty(cards)) {
    return <MarkdownTable copyable={COPYABLE} node={node} {...props} />;
  }
  return (
    <CardTableContext.Provider value={cards}>
      <MarkdownTable copyable={COPYABLE} node={node} {...props} className={styles.cardTable} />
    </CardTableContext.Provider>
  );
}

function AskTableRow({ node, ...props }: MarkdownNodeProps & ComponentProps<'tr'>) {
  const tableCards = useContext(CardTableContext);
  const openCard = useOpenAskCard();
  // The row's card is the first one it links to — the title column, in every table the agent
  // writes. A header row links to nothing and stays a header.
  const card = useMemo(() => cardLinksIn(node)[0], [node]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLTableRowElement>) => {
      // A link in the row does its own thing — the card's title opens the card, a modified click
      // on it opens a tab, and any other link goes where it points — so the row stays out of it.
      if (!isNil((event.target as HTMLElement).closest('a'))) {
        return;
      }
      if (!isNil(card) && !isNil(tableCards)) {
        openCard(card, tableCards);
      }
    },
    [card, tableCards, openCard],
  );

  if (isNil(tableCards) || isNil(card)) {
    return <tr {...props} />;
  }
  return <tr {...props} className={styles.cardRow} onClick={handleClick} data-card-row />;
}

function AskLink(props: MarkdownNodeProps & ComponentProps<'a'>) {
  const { href } = props;
  // The agent quotes a title inside its link — `[“Let’s move!”](…)` — so the quotes would be
  // underlined as part of it. They are the sentence's, not the link's, and go outside it.
  const { open, inner: children, close } = splitQuotes(props.children as ReactNode);
  const tableCards = useContext(CardTableContext);
  // Set only on the Ask page, which is where a card link opens the card beside the conversation
  // rather than in its workspace.
  const isAskPage = !isNil(useContext(AskCardsContext));
  const openCard = useOpenAskCard();
  const card = useMemo(() => (isNil(href) ? undefined : toCardLink(href)), [href]);
  const opensItself = !isNil(card) && (!isNil(tableCards) || isAskPage);

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (isNil(card)) {
        return;
      }
      // A modified click is a request for a new tab, which the href still serves.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      openCard(card, tableCards ?? [card]);
    },
    [card, tableCards, openCard],
  );

  // A link to anything but a card is a link. So is a card in prose in the floating panel: there
  // is no pane to open it beside, and its workspace is where it goes.
  if (!opensItself) {
    return (
      <>
        {open}
        <MarkdownLink {...props}>{children}</MarkdownLink>
        {close}
      </>
    );
  }

  // `data-opens-itself` keeps the transcript's own link handling from navigating first.
  return (
    <>
      {open}
      <a
        href={href}
        // In a table of cards the title is the row's way in, drawn as the table view draws a title.
        className={isNil(tableCards) ? undefined : styles.cardTitle}
        onClick={handleOpen}
        data-opens-itself
      >
        {children}
      </a>
      {close}
    </>
  );
}

const COMPONENTS: MarkdownComponents = { table: AskTable, tr: AskTableRow, a: AskLink };

/** The cards a node links to, each once, in the order it names them. */
function cardLinksIn(node: Nodes | undefined): AskCardLink[] {
  const links: AskCardLink[] = [];
  const visit = (current: Nodes) => {
    if (current.type === 'element' && current.tagName === 'a') {
      const href = (current as Element).properties?.href;
      const link = typeof href === 'string' ? toCardLink(href) : undefined;
      if (!isNil(link)) {
        links.push(link);
      }
    }
    if ('children' in current) {
      current.children.forEach(child => visit(child as Nodes));
    }
  };
  if (!isNil(node)) {
    visit(node);
  }
  return uniqBy(links, link => link.cardId);
}
